import type {IHttpResponse, IPromise, IQService} from 'angular';
import * as angular from 'angular';
import * as _ from 'lodash';
import {DateTime} from 'luxon';
import {inline} from './array-utils.fp';
import {isThenable} from './custom-utils';
import type {ActionResponse, Identified, Record} from 'typings/portaro.be.types';
import {isActionResponse, isIHttpResponse} from 'shared/utils/types-utils';

// eslint-disable-next-line
const _isDefined = _.negate(_.isUndefined);

const $log = angular.injector(['ng']).get('$log');

/**
 * @module data-service-utils
 * @name unRestangularize
 * @kind decorator
 *
 * @description
 * Decorator transform method output from restangularized element to plain data.
 *
 * @example
 * ```js
 *      class DataService {
 *
 *          constructor(Restangular) {
 *              this.Restangular = Restangular;
 *          }
 *
 *          \@unRestangularize
 *          getData() {
 *              return this.Restangular
 *                  .all('data')
 *                  .get();
 *          }
 *      }
 * ```
 */
export function unRestangularize() {
    return (target, name, descriptor) => {
        const original = descriptor.value;

        if (typeof original === 'function') {
            descriptor.value = function (...args) {
                const result = original.apply(this, args);

                if (!isThenable(result)) {
                    $log.warn('@unRestangularize decorator can be used only on promise-returning methods.');
                    return result;
                }

                return result.then((data: Restangular.IElement) => data?.plain());
            };
        }

        return descriptor;
    };
}

/**
 * @module data-service-utils
 * @name defaultIdMapper
 * @kind function
 *
 * @template ID
 * @param {Identified<ID>} object Object to transform
 * @return {ID}
 *
 * @description
 * default id mapper for function @see {@link transferify}
 */
export function defaultIdMapper<ID>(object: Identified<ID>): ID {
    return object.id;
}

/**
 * @module data-service-utils
 * @name transferify
 * @kind function
 *
 * @param {*} object Object to transform
 * @param {boolean=} idfyAlsoFirstLevel If `true`, it transform first level also.
 * @param idMapper {(_: Identified<ID>) => any} mapper that maps object to its id (default: obj => obj.id)
 * @return {*}
 *
 * @description
 * Function transforms object to ID. If some object contains property `id`, function returns its value.
 * If `obj` is JS Date, function transform it to ISO 8601 format.
 */
export function transferify<ID>(object: any, idfyAlsoFirstLevel = false, idMapper: ((_: Identified<ID>) => any) = defaultIdMapper): any {
    const transferifyRecursion = (obj, idfy = true) => {
        if (_.isUndefined(obj) || obj === null) {
            return null;
        }

        if (idfy && _isDefined(obj.id)) {
            return idMapper(obj);
        }

        if (typeof obj === 'object' && obj instanceof Date) {
            return DateTime.fromJSDate(obj).toISO();
        }

        if (_.isArray(obj)) {
            for (let i = 0; i < obj.length; i++) {
                obj[i] = transferifyRecursion(obj[i]);
            }
            return obj;
        }

        if (_.isObject(obj)) {
            for (const propertyName in obj) {
                if (propertyName.startsWith('$')) { // removing angular (e.g. Resource object) properties
                    delete obj[propertyName];
                } else if (Object.hasOwn(obj, propertyName)) {
                    obj[propertyName] = transferifyRecursion(obj[propertyName]);
                }
            }
            return obj;
        }

        return obj;
    };

    const copy = _.cloneDeep(object);
    return transferifyRecursion(copy, idfyAlsoFirstLevel);
}

export interface DataContainingResponse<T> {
    data: T;

    [x: string]: any;
}

/**
 * @module data-service-utils
 * @name responseToData
 * @kind function
 *
 * @param {Object} res Response
 *
 * @return {*}
 *
 * @description
 * Returns data from response wrapper
 */
export function responseToData<T>(res: DataContainingResponse<T> | IHttpResponse<T>): T {
    return res.data;
}

/**
 * @module data-service-utils
 * @name getResponseToRejectedData
 * @kind function
 *
 * @return {function(object): ng.service.$q}
 *
 * @description
 * Returns function that returns rejecting promise with unwrapped data from response. (Or something that)
 */
export function getResponseToRejectedData<T>($q: IQService): (res: DataContainingResponse<T>) => IPromise<T> {
    return (res) => {
        return $q.reject(res.data);
    };
}

/**
 * @module data-service-utils
 * @name getErrorMessage
 * @kind function
 *
 * @param {object} data
 *
 * @return {string|object}
 *
 * @description
 * Returns error message(s) from response or plain data
 */
export function getErrorMessage(data) {
    if (data && data.data) {
        data = data.data;
    }

    if (data && data.text) {
        return data.text;
    }

    if (angular.isArray(data)) {
        return inline('; ')(data
            .map(getErrorMessage));
    }

    if (data) {
        return data;
    }

    return 'Unknown error';
}

/**
 * @module data-service-utils
 * @name transferify
 * @kind function
 *
 * @return {ActionResponse}
 * @param response {ActionResponse | IHttpResponse<ActionResponse>}
 *
 * @description
 * Transforms ActionResponse or HttpResponse containing ActionResponse to ActionResponse
 */
export function getActionResponse(response: ActionResponse | IHttpResponse<ActionResponse>): ActionResponse {
    if (isActionResponse(response)) {
        return response;
    } else if (isIHttpResponse(response)) {
        return response.data;
    }

    throw new Error('Response is not IHttpResponse or ActionResponse');
}

export function getRecordUrlId(object: Record): string {
    return object.id;
}

export function getRecordPath(recordId: string): string {
    return `/records/${recordId}`;
}
